import React, { useState, useEffect } from 'react';
import {
    ArrowLeftRight, CheckSquare, Square, Minus, Plus,
    Search, ChevronLeft, Package,
} from 'lucide-react';
import { Transaction, Product, ToastMessage } from '../../types';

interface ExchangeSelection {
    selected: boolean;
    newSize: string;
    exchangeQty: number;
}

interface ExchangeViewProps {
    /** Pre-selected transaction when navigating from the Transactions tab. Null = show search. */
    prefillTransaction: Transaction | null;
    transactions: Transaction[];
    inventory: Product[];
    currentUser: string;
    onExchangeComplete: () => Promise<void>;
    addToast: (type: ToastMessage['type'], title: string, message: string) => void;
    /** Called when user dismisses the active transaction so App can clear the prefill state. */
    onClearPrefill: () => void;
}

const ExchangeView: React.FC<ExchangeViewProps> = ({
    prefillTransaction,
    transactions,
    inventory,
    currentUser,
    onExchangeComplete,
    addToast,
    onClearPrefill,
}) => {
    const [activeTxn, setActiveTxn] = useState<Transaction | null>(prefillTransaction);
    const [searchTerm, setSearchTerm] = useState('');
    const [selections, setSelections] = useState<Record<string, ExchangeSelection>>({});
    const [loading, setLoading] = useState(false);

    // Sync when App pushes a new prefill (e.g. user clicks exchange in Transactions tab again)
    useEffect(() => {
        if (prefillTransaction) setActiveTxn(prefillTransaction);
    }, [prefillTransaction]);

    // Reset selections whenever the active transaction changes
    useEffect(() => {
        if (!activeTxn) { setSelections({}); return; }
        setSelections(
            Object.fromEntries(
                activeTxn.items.map((item, idx) => [
                    `${item._id}-${idx}`,
                    { selected: false, newSize: '', exchangeQty: 1 },
                ])
            )
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTxn?.id]);

    // ── Search / filter eligible transactions ─────────────────────────────────
    const eligibleTransactions = transactions.filter(t => {
        if (t.isExchanged || t.isReturned) return false;
        if (String(t.type).toLowerCase() !== 'sale') return false;
        const term = searchTerm.toLowerCase();
        return (
            t.id.toLowerCase().includes(term) ||
            (t.customer?.name || '').toLowerCase().includes(term)
        );
    });

    // ── Exchange logic (mirrors ExchangeModal) ────────────────────────────────
    const getAvailableSizes = (productId: string, currentSize: string) => {
        const product = inventory.find(p => p.id === productId);
        if (!product?.stocks) return [];
        return product.stocks
            .filter(s => String(s.size) !== String(currentSize) && Number(s.stock ?? 0) > 0)
            .map(s => ({ size: String(s.size), stock: Number(s.stock ?? 0) }));
    };

    const getTargetStock = (productId: string, size: string) => {
        const product = inventory.find(p => p.id === productId);
        const entry = product?.stocks?.find(s => String(s.size) === String(size));
        return Number(entry?.stock ?? 0);
    };

    const toggleItem = (key: string, maxQty: number) => {
        setSelections(prev => ({
            ...prev,
            [key]: {
                selected: !prev[key]?.selected,
                newSize: !prev[key]?.selected ? prev[key]?.newSize || '' : '',
                exchangeQty: Math.min(Math.max(prev[key]?.exchangeQty || 1, 1), Math.max(maxQty, 1)),
            },
        }));
    };

    const setNewSize = (key: string, size: string) => {
        setSelections(prev => ({
            ...prev,
            [key]: { ...prev[key], selected: true, newSize: String(size) },
        }));
    };

    const setExchangeQty = (key: string, value: number, maxQty: number) => {
        const safeQty = Math.min(Math.max(Number.isFinite(value) ? value : 1, 1), Math.max(maxQty, 1));
        setSelections(prev => ({ ...prev, [key]: { ...prev[key], exchangeQty: safeQty } }));
    };

    const selectedEntries = (Object.entries(selections) as [string, ExchangeSelection][]).filter(([, s]) => s.selected);
    const canConfirm =
        selectedEntries.length > 0 &&
        selectedEntries.every(([, s]) => s.newSize !== '' && Number(s.exchangeQty) >= 1);

    const handleConfirm = async () => {
        if (!activeTxn || loading) return;

        const pendingExchanges = activeTxn.items
            .map((item, idx) => {
                const key = `${item._id}-${idx}`;
                const selection = selections[key];
                if (!selection?.selected || !selection?.newSize) return null;
                return {
                    item,
                    oldSize: String(item.selectedSize?.size ?? ''),
                    newSize: String(selection.newSize),
                    quantity: Number(selection.exchangeQty || 0),
                    soldQty: Number(item.quantity || 0),
                    availableTargetStock: getTargetStock(item.id, selection.newSize),
                };
            })
            .filter(Boolean) as {
                item: Transaction['items'][number];
                oldSize: string;
                newSize: string;
                quantity: number;
                soldQty: number;
                availableTargetStock: number;
            }[];

        if (pendingExchanges.length === 0) {
            addToast('error', 'No Items Selected', 'Please select at least one item to exchange.');
            return;
        }

        const invalidSameSize = pendingExchanges.find(x => x.oldSize === x.newSize);
        if (invalidSameSize) {
            addToast('error', 'Invalid Exchange', `${invalidSameSize.item.name}: new size must differ from current size.`);
            return;
        }

        const invalidQty = pendingExchanges.find(x => x.quantity < 1 || x.quantity > x.soldQty);
        if (invalidQty) {
            addToast('error', 'Invalid Quantity', `${invalidQty.item.name}: exchange quantity must be between 1 and ${invalidQty.soldQty}.`);
            return;
        }

        const stockExceeded = pendingExchanges.find(x => x.quantity > x.availableTargetStock);
        if (stockExceeded) {
            addToast('error', 'Insufficient Stock', `${stockExceeded.item.name}: only ${stockExceeded.availableTargetStock} unit(s) available in size ${stockExceeded.newSize}.`);
            return;
        }

        setLoading(true);
        try {
            let successCount = 0;
            for (const exchange of pendingExchanges) {
                const { item, oldSize, newSize, quantity, availableTargetStock } = exchange;
                if (quantity > availableTargetStock) {
                    addToast('error', 'Insufficient Stock', `${item.name}: requested ${quantity}, but only ${availableTargetStock} available in size ${newSize}.`);
                    continue;
                }

                const payload = {
                    transactionId: activeTxn.id,
                    productId: item.id,
                    productName: item.name,
                    oldSize,
                    newSize,
                    quantity,
                    performedBy: currentUser,
                };

                const res = await fetch(`${process.env.TRANSACTION_BASE_URL}/exchange-size`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                const data = await res.json();
                if (!res.ok || !data.OK) {
                    addToast('error', 'Exchange Failed', data.MSG || `${item.name}: unable to process exchange.`);
                    continue;
                }
                successCount++;
            }

            if (successCount > 0) {
                addToast('success', 'Exchange Complete', `${successCount} item(s) exchanged successfully.`);
                await onExchangeComplete();
                setActiveTxn(null);
                onClearPrefill();
            }
        } catch (err) {
            console.error('Exchange error:', err);
            addToast('error', 'Server Error', 'Could not complete exchange.');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setActiveTxn(null);
        onClearPrefill();
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="p-8 h-full overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
            <div className="max-w-3xl mx-auto space-y-6">

                {/* Header */}
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <ArrowLeftRight size={22} className="text-indigo-500" />
                            Size Exchange
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {activeTxn
                                ? `Transaction ${activeTxn.id.slice(0, 8)}… · ${activeTxn.businessDate}`
                                : 'Select a transaction to begin the exchange.'}
                        </p>
                    </div>
                    {activeTxn && (
                        <button
                            onClick={handleClear}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
                        >
                            <ChevronLeft size={15} />
                            Change Transaction
                        </button>
                    )}
                </header>

                {/* ── No transaction selected: search + list ─────────────── */}
                {!activeTxn && (
                    <div className="space-y-4">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Search by transaction ID or customer name…"
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white transition-all"
                                />
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                            {eligibleTransactions.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 text-gray-300 dark:text-gray-700">
                                    <ArrowLeftRight size={40} className="mb-3 opacity-40" />
                                    <p className="text-sm font-medium text-gray-400">No eligible transactions found.</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                                    {eligibleTransactions.slice(0, 30).map(txn => (
                                        <button
                                            key={txn.id}
                                            onClick={() => setActiveTxn(txn)}
                                            className="w-full text-left p-4 hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors flex items-center justify-between gap-4 group"
                                        >
                                            <div>
                                                <p className="text-xs font-black text-gray-900 dark:text-white font-mono">
                                                    {txn.id.slice(0, 8)}…
                                                </p>
                                                <p className="text-[10px] text-gray-400 mt-0.5">
                                                    {txn.businessDate} · {txn.customer?.name || 'Walk-in'}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-[10px] text-gray-400 flex items-center gap-1">
                                                    <Package size={11} />
                                                    {txn.items.length} item(s)
                                                </span>
                                                <span className="text-sm font-black text-indigo-600 dark:text-indigo-400">
                                                    ₨ {txn.total.toLocaleString()}
                                                </span>
                                                <ArrowLeftRight
                                                    size={14}
                                                    className="text-gray-300 group-hover:text-indigo-400 transition-colors"
                                                />
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Transaction selected: exchange UI ──────────────────── */}
                {activeTxn && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                        <div className="px-6 pt-5 pb-2 border-b border-gray-50 dark:border-gray-700/50">
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                                Select items · choose new size · set exchange quantity
                            </p>
                        </div>

                        <div className="p-4 space-y-2">
                            {activeTxn.items.map((item, idx) => {
                                const key = `${item._id}-${idx}`;
                                const sel = selections[key];
                                const currentSize = String(item.selectedSize?.size ?? '');
                                const availableSizes = getAvailableSizes(item.id, currentSize);
                                const isSelected = sel?.selected ?? false;
                                const soldQty = Number(item.quantity || 0);
                                const chosenTargetStock = sel?.newSize ? getTargetStock(item.id, sel.newSize) : 0;
                                const qtyExceeded = Number(sel?.exchangeQty || 0) > chosenTargetStock && !!sel?.newSize;

                                return (
                                    <div
                                        key={key}
                                        className={`rounded-2xl border transition-all ${
                                            isSelected
                                                ? 'border-indigo-400 ring-2 ring-indigo-500/10 bg-indigo-50/20 dark:bg-indigo-900/10'
                                                : 'border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900'
                                        }`}
                                    >
                                        {/* Item row */}
                                        <div className="p-4 flex items-center gap-3">
                                            <button
                                                onClick={() => toggleItem(key, soldQty)}
                                                className={`transition-colors shrink-0 ${
                                                    isSelected ? 'text-indigo-600' : 'text-gray-300 hover:text-gray-400'
                                                }`}
                                            >
                                                {isSelected ? <CheckSquare size={20} /> : <Square size={20} />}
                                            </button>

                                            {item.image && (
                                                <img
                                                    src={item.image}
                                                    className="w-10 h-10 rounded-xl object-cover shrink-0"
                                                    alt={item.name}
                                                />
                                            )}

                                            <div className="flex-1 min-w-0">
                                                <p className="font-black text-sm text-gray-900 dark:text-white truncate leading-tight">
                                                    {item.name}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                    <span className="text-[9px] font-black text-indigo-500 uppercase bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-md">
                                                        {currentSize || '—'}
                                                    </span>
                                                    <span className="text-[9px] font-black text-gray-400 uppercase">
                                                        Sold × {soldQty}
                                                    </span>
                                                </div>
                                            </div>

                                            {isSelected && sel?.newSize && (
                                                <div className="flex items-center gap-2 shrink-0 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1.5 rounded-xl">
                                                    <ArrowLeftRight size={12} className="text-indigo-400" />
                                                    <span className="text-sm font-black text-indigo-600 dark:text-indigo-400">
                                                        {sel.newSize}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Expanded controls */}
                                        {isSelected && (
                                            <div className="px-4 pb-4 space-y-4">
                                                {availableSizes.length === 0 ? (
                                                    <p className="text-[9px] font-black text-red-400 uppercase tracking-wider bg-red-50 dark:bg-red-900/10 px-3 py-2 rounded-xl">
                                                        No other sizes in stock for this article
                                                    </p>
                                                ) : (
                                                    <>
                                                        {/* Size picker */}
                                                        <div>
                                                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-2">
                                                                Available sizes
                                                            </p>
                                                            <div className="flex flex-wrap gap-2">
                                                                {availableSizes.map(({ size, stock }) => (
                                                                    <button
                                                                        key={size}
                                                                        onClick={() => {
                                                                            setNewSize(key, size);
                                                                            if ((sel?.exchangeQty || 1) > stock) {
                                                                                setExchangeQty(key, stock, soldQty);
                                                                            }
                                                                        }}
                                                                        className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase transition-all border ${
                                                                            sel?.newSize === size
                                                                                ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-200 dark:shadow-none scale-105'
                                                                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-indigo-400 hover:text-indigo-600'
                                                                        }`}
                                                                    >
                                                                        {size}
                                                                        <span className={`ml-1.5 text-[8px] font-black ${sel?.newSize === size ? 'text-indigo-200' : 'text-gray-400'}`}>
                                                                            {stock}
                                                                        </span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Quantity stepper */}
                                                        <div>
                                                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-2">
                                                                Exchange quantity
                                                            </p>
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setExchangeQty(key, Number(sel?.exchangeQty || 1) - 1, soldQty)}
                                                                    className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-indigo-600 transition-colors"
                                                                >
                                                                    <Minus size={14} />
                                                                </button>
                                                                <input
                                                                    type="number"
                                                                    min={1}
                                                                    max={soldQty}
                                                                    step={1}
                                                                    value={sel?.exchangeQty || 1}
                                                                    onChange={e => setExchangeQty(key, Number(e.target.value), soldQty)}
                                                                    className="w-20 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-center text-sm font-black text-gray-900 dark:text-white"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setExchangeQty(key, Number(sel?.exchangeQty || 1) + 1, soldQty)}
                                                                    className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-indigo-600 transition-colors"
                                                                >
                                                                    <Plus size={14} />
                                                                </button>
                                                                <span className="text-[9px] font-black uppercase text-gray-400 ml-2">
                                                                    Max {soldQty}
                                                                </span>
                                                            </div>

                                                            {sel?.newSize ? (
                                                                <p className={`mt-2 text-[9px] font-black uppercase tracking-wider ${qtyExceeded ? 'text-red-500' : 'text-gray-400'}`}>
                                                                    Available in size {sel.newSize}: {chosenTargetStock}
                                                                </p>
                                                            ) : (
                                                                <p className="mt-2 text-[9px] font-black uppercase tracking-wider text-gray-400">
                                                                    Select a target size to validate stock
                                                                </p>
                                                            )}

                                                            {qtyExceeded && (
                                                                <p className="mt-1 text-[9px] font-black uppercase tracking-wider bg-red-50 dark:bg-red-900/10 text-red-500 px-3 py-2 rounded-xl">
                                                                    Requested quantity exceeds available stock in selected size
                                                                </p>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="p-5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4">
                            <div>
                              <p className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                                  {selectedEntries.length === 0
                                      ? 'No items selected'
                                      : `${selectedEntries.length} item(s) queued for exchange`}
                              </p>
                              {/* BUG #23 fix: explain why CONFIRM is disabled when items are queued */}
                              {selectedEntries.length > 0 && !canConfirm && (
                                <p className="text-[9px] font-black uppercase tracking-widest text-amber-500 mt-1">
                                  Select a new size for each queued item to enable confirm
                                </p>
                              )}
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleClear}
                                    className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-black text-[10px] uppercase tracking-widest transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    disabled={!canConfirm || loading}
                                    onClick={handleConfirm}
                                    className={`px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-[0.12em] transition-all flex items-center gap-2 active:scale-95 ${
                                        canConfirm && !loading
                                            ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200 dark:shadow-none'
                                            : 'bg-gray-100 dark:bg-gray-800 text-gray-300 dark:text-gray-700 cursor-not-allowed'
                                    }`}
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                            Processing
                                        </>
                                    ) : (
                                        <>
                                            <ArrowLeftRight size={13} strokeWidth={2.5} />
                                            Confirm Exchange
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExchangeView;
